import { type FC, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { useData } from "../context/DataContext";
import QrCodeDisplayModal from "../components/QrCodeDisplayModal";
import type { QrCode } from "../types";
import QrCodeListDisplayModal from "../components/QrCodeListDisplayModal";
import { useParams } from "react-router-dom";

interface TimeSlot {
    from: string;
    to: string;
}

const initialFormData = {
    namesList: '',
    name: '',
    idCategory: '',
    idClient: '',
    note: '',
    validFrom: '',
    validTo: '',
    timeSlots: [{ id: -1, from: '00:00', to: '23:59' }],
    usageLimit: 'unlimited',
    usageLimitFasce: 'unlimited',
    maxUses: -1,
    maxUsesFasce: -1,
    isOneShot: false,
    quantity: 1
};

// --- Helper Icons ---
const Icons = {
    Calendar: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
    Clock: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    User: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>,
    Category: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>,
    Text: <svg className="w-5 h-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" /></svg>,
    Plus: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>,
    Trash: <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
};

const CreateQrPage: FC = () => {
    const { categories, clients, isLoading: isDataLoading, error: dataError } = useData();
    const [formData, setFormData] = useState(initialFormData);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [creationMode, setCreationMode] = useState<string>('single');

    const [createdQrCode, setCreatedQrCode] = useState<QrCode | null>(null);
    const [createdQrCodesList, setCreatedQrCodesList] = useState<QrCode[] | null>(null);
    const [createdQrCodesDifferentList, setCreatedQrCodesDifferentList] = useState<QrCode[] | null>(null);
    const { id, idshot } = useParams();

    useEffect(() => {
        if(id){
            fetchSingleQrCode()
        }
    }, [id]);

    useEffect(() => {
        if(idshot){
            fetchSingleQrCode() // Assuming logic implies using same fetch or similar for idshot if present
        }
    }, [idshot]);

    const fetchSingleQrCode = async () => {
        // Logic remains same, careful with id vs idshot usage if needed
        const targetId = id || idshot;
        if(!targetId) return;

        const response = await api.get('/api/qrcodes/get/' + targetId)
        console.log(response)
        if(response && response.status === 200 && response.data.data) {
            const qrCodeDto: QrCode = response.data.data
            setFormData({
                namesList: '',
                name: qrCodeDto.name,
                idCategory: qrCodeDto.idCategory.toString(),
                idClient: qrCodeDto.idClient.toString(),
                note: qrCodeDto.note || '',
                validFrom: qrCodeDto.validFrom,
                validTo: qrCodeDto.validTo,
                timeSlots: qrCodeDto.availability.map(value => { return {id: value.id, from: value.from, to: value.to}}),
                usageLimit: qrCodeDto.maxUses === -1 ? 'unlimited' : 'limited',
                usageLimitFasce: qrCodeDto.maxUsesFasce === -1 ? 'unlimited' : 'limited',
                maxUses: qrCodeDto.maxUses,
                maxUsesFasce: qrCodeDto.maxUsesFasce,
                isOneShot: false,
                quantity: 1
            })
        }
    }

    const namesCount = useMemo(() => {
        if (!formData.namesList) return 0;
        return formData.namesList.split('\n').filter(name => name.trim() !== '').length;
    }, [formData.namesList]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (name === 'isOneShot') {
            setFormData(prev => ({
                ...prev,
                isOneShot: value === 'yes'
            }));
        } else if (name === 'namesList') {
            const lines = value.split('\n');
            if (lines.length > 10) {
                const truncatedValue = lines.slice(0, 10).join('\n');
                setFormData(prev => ({
                    ...prev,
                    [name]: truncatedValue
                }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    [name]: value
                }));
            }
        }else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    // Helper for custom radio cards
    const handleRadioChange = (name: string, value: string) => {
        // Create a synthetic event or call logic directly
        const syntheticEvent = { target: { name, value, type: 'radio' } } as any;
        handleInputChange(syntheticEvent);
    };


    const handleTimeSlotChange = (index: number, field: 'from' | 'to', value: string) => {
        const newSlots = [...formData.timeSlots];
        newSlots[index][field] = value;
        setFormData(prev => ({ ...prev, timeSlots: newSlots }));
    };

    const addTimeSlot = () => {
        setFormData(prev => ({ ...prev, timeSlots: [...prev.timeSlots, { id: -1, from: '', to: '' }] }));
    };

    const removeTimeSlot = (index: number) => {
        if (formData.timeSlots.length > 1) {
            const newSlots = formData.timeSlots.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, timeSlots: newSlots }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        let calculatedMaxUses = -1;
        let calculatedMaxUsesFasce = -1;

        if (creationMode === "single" && formData.isOneShot && (formData.quantity <= 0 || !Number.isInteger(Number(formData.quantity)))) {
            setSubmitError("Inserisci un numero intero positivo per la quantità.");
            setIsSubmitting(false);
            return;
        }

        if((formData.validFrom !== "" && formData.validTo === "") || (formData.validTo !== "" && formData.validFrom === "")){
            setSubmitError(formData.validTo === "" ? "Inserire data di fine validita'" : "Inserire data di inizio validita'");
            setIsSubmitting(false);
            return;
        }

        const fixedTimeSlots = formData.timeSlots.map(slot => ({
            ...slot,
            to: slot.to === "00:00" ? "23:59" : slot.to
        }));

        for (let slot of fixedTimeSlots) {
            const [fromH, fromM] = slot.from.split(":").map(Number);
            const [toH, toM] = slot.to.split(":").map(Number);

            const fromMinutes = fromH * 60 + fromM;
            const toMinutes = toH * 60 + toM;

            if (toMinutes <= fromMinutes) {
                setSubmitError("Orari sbagliati: L'orario di fine deve essere successivo a quello di inizio.");
                setIsSubmitting(false);
                return;
            }
        }


        if (creationMode === "single" && formData.isOneShot) {
            calculatedMaxUses = 1;
            calculatedMaxUsesFasce = 1;
        } else {
            calculatedMaxUses = formData.usageLimit === 'unlimited' ? -1 : Number(formData.maxUses);
            calculatedMaxUsesFasce = formData.usageLimitFasce === 'unlimited' ? -1 : Number(formData.maxUsesFasce);
        }

        const payload = id || idshot ? {
            id: Number(id),
            namesList: creationMode === "single" ? [] : formData.namesList.split("\n"),
            name: formData.name,
            idCategory: Number(formData.idCategory),
            idClient: formData.idClient ? Number(formData.idClient) : -1,
            note: formData.note,
            validFrom: formData.validFrom || null,
            validTo: formData.validTo || null,
            qrCodeAvailabilityDto: fixedTimeSlots,
            maxUses: calculatedMaxUses,
            maxUsesFasce: calculatedMaxUsesFasce,
            oneShot: creationMode === "single" && formData.isOneShot,
            quantity: formData.isOneShot && creationMode === "single" ? Number(formData.quantity) : 1
        } : {
            namesList: creationMode === "single" ? [] : formData.namesList.split("\n"),
            name: formData.name,
            idCategory: Number(formData.idCategory),
            idClient: formData.idClient ? Number(formData.idClient) : -1,
            note: formData.note,
            validFrom: formData.validFrom || null,
            validTo: formData.validTo || null,
            qrCodeAvailabilityDto: fixedTimeSlots,
            maxUses: calculatedMaxUses,
            maxUsesFasce: calculatedMaxUsesFasce,
            oneShot: creationMode === "single" && formData.isOneShot,
            quantity: formData.isOneShot && creationMode === "single" ? Number(formData.quantity) : 1
        };

        if ((creationMode === "single" && !payload.name) || (creationMode === "multiple" && namesCount === 0)) {
            setSubmitError("Nome e' un campo obbligatorio");
            setIsSubmitting(false);
            return;
        }

        if(!payload.idCategory){
            setSubmitError("Categoria è un campo obbligatorio");
            setIsSubmitting(false);
            return;
        }

        try {
            const endpoint_string = id ? '/api/qrcodes/update' : idshot ? '/api/qrcodes/updateshot' : '/api/qrcodes/add';

            const endpoint = creationMode === "single" && formData.isOneShot ? '/api/qrcodes/addMultiple' : endpoint_string;

            const response = await api.post(endpoint, payload);
            if (response.data.status) {
                if(id){
                    setCreatedQrCode(response.data.data);
                    setCreatedQrCodesList(null);
                    setCreatedQrCodesDifferentList(null);
                } else if (creationMode === "single") {
                    if (formData.isOneShot) {
                        setCreatedQrCodesList(response.data.data);
                        setCreatedQrCode(null);
                        setCreatedQrCodesDifferentList(null);
                    } else {
                        setCreatedQrCode(response.data.data[0]);
                        setCreatedQrCodesList(null);
                        setCreatedQrCodesDifferentList(null);
                    }
                } else {
                    setCreatedQrCode(null);
                    setCreatedQrCodesList(null);
                    setCreatedQrCodesDifferentList(response.data.data);
                }

                if(!id) {
                    setFormData(prev => ({
                        ...initialFormData,
                        // Maintain some defaults if desired, or reset completely
                        validFrom: prev.validFrom,
                        validTo: prev.validTo,
                        timeSlots: prev.timeSlots,
                        usageLimit: prev.usageLimit,
                        usageLimitFasce: prev.usageLimitFasce,
                        maxUses: prev.maxUses,
                        maxUsesFasce: prev.maxUsesFasce,
                        isOneShot: prev.isOneShot,
                        quantity: prev.quantity,
                        idClient: prev.idClient,
                        idCategory: prev.idCategory,
                    }));
                }

            } else {
                throw new Error(response.data.message || "Si è verificato un errore sconosciuto.");
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || "Errore durante la creazione del QR Code.";
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isDataLoading) {
        return <div className="flex justify-center items-center h-screen text-slate-300 animate-pulse">Caricamento dati...</div>;
    }

    if (dataError) {
        return (
            <div className="flex justify-center mt-10">
                <div className="p-6 bg-red-900/40 border border-red-700/50 backdrop-blur text-red-200 rounded-2xl max-w-lg text-center shadow-xl">
                    <p className="font-semibold">Errore caricamento dati</p>
                    <p className="text-sm mt-1 opacity-80">{dataError}</p>
                </div>
            </div>
        );
    }

    const handleModeChange = (value: string) => {
        setCreationMode(value)
        if(value === "multiple")
            setFormData(prev => ({
                ...prev,
                isOneShot: false
            }));
    }

    return (
        <div className="max-w-5xl mx-auto pb-12 animate-fade-in space-y-8">
            {/* Header */}
            <div>
                <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400 mb-2">
                    {id ? 'Modifica QR Code' : 'Crea Nuovo QR Code'}
                </h1>
                <p className="text-slate-400 text-lg">
                    {id ? 'Aggiorna i dettagli del QR Code esistente.' : 'Configura i dettagli per generare un nuovo pass.'}
                </p>
            </div>

            {/* Mode Switcher */}
            {id === undefined && (
                <div className="flex justify-center">
                    <div className="bg-slate-900/80 p-1 rounded-xl inline-flex relative shadow-inner border border-slate-700/50">
                        <button
                            type="button"
                            onClick={() => handleModeChange('single')}
                            className={`relative z-10 px-8 py-2.5 text-sm font-bold rounded-lg transition-all duration-300 ${
                                creationMode === 'single' ? 'text-white shadow-lg bg-sky-600' : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            Creazione Singola
                        </button>
                        <button
                            type="button"
                            onClick={() => handleModeChange('multiple')}
                            className={`relative z-10 px-8 py-2.5 text-sm font-bold rounded-lg transition-all duration-300 ${
                                creationMode === 'multiple' ? 'text-white shadow-lg bg-sky-600' : 'text-slate-400 hover:text-white'
                            }`}
                        >
                            Creazione Multiriga
                        </button>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-8">

                {/* Section 1: Dati Principali */}
                <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-3xl shadow-xl overflow-hidden">
                    <div className="p-6 md:p-8 space-y-6">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-700/50">
                            <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center text-sky-400">
                                {Icons.Text}
                            </div>
                            <h3 className="text-xl font-bold text-white">Dati Principali</h3>
                        </div>

                        <div className="grid grid-cols-1 gap-6">
                            {creationMode === "single" ? (
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2 ml-1">Nome / Descrizione</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{Icons.Text}</div>
                                        <input
                                            type="text" id="name" name="name"
                                            value={formData.name} onChange={handleInputChange}
                                            placeholder="Es. Ingresso Concerto Roma"
                                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all placeholder-slate-500"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <label htmlFor="namesList" className="block text-sm font-medium text-slate-300 mb-2 ml-1">
                                        Nomi / Descrizioni <span className="text-slate-500 text-xs font-normal">(uno per riga, max 10)</span>
                                    </label>
                                    <textarea
                                        id="namesList" name="namesList" rows={5}
                                        value={formData.namesList} onChange={handleInputChange}
                                        className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all placeholder-slate-500"
                                        placeholder="Es. Ingresso VIP Mario Rossi&#10;Ingresso Standard Anna Bianchi&#10;..."
                                    />
                                    <div className="flex justify-end mt-2">
                                        <span className={`text-xs px-2 py-1 rounded-md ${namesCount > 0 ? 'bg-emerald-500/20 text-emerald-300' : 'text-slate-500'}`}>
                                            Generazione di {namesCount} QR Code
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="idCategory" className="block text-sm font-medium text-slate-300 mb-2 ml-1">Categoria</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{Icons.Category}</div>
                                        <select
                                            id="idCategory" name="idCategory"
                                            value={formData.idCategory} onChange={handleInputChange}
                                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all appearance-none"
                                        >
                                            <option value="">Seleziona...</option>
                                            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-500">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="idClient" className="block text-sm font-medium text-slate-300 mb-2 ml-1">Associa a Cliente (opzionale)</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{Icons.User}</div>
                                        <select
                                            id="idClient" name="idClient"
                                            value={formData.idClient} onChange={handleInputChange}
                                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl py-3 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all appearance-none"
                                        >
                                            <option value="">Nessun cliente</option>
                                            {clients.map(cli => <option key={cli.id} value={cli.id}>{cli.name}</option>)}
                                        </select>
                                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none text-slate-500">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label htmlFor="note" className="block text-sm font-medium text-slate-300 mb-2 ml-1">Note interne (opzionale)</label>
                                <textarea
                                    id="note" name="note" rows={3}
                                    value={formData.note} onChange={handleInputChange}
                                    className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent transition-all placeholder-slate-500"
                                    placeholder="Dettagli aggiuntivi..."
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Regole di Validità */}
                <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-3xl shadow-xl overflow-hidden">
                    <div className="p-6 md:p-8 space-y-8">
                        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-700/50">
                            <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                                {Icons.Clock}
                            </div>
                            <h3 className="text-xl font-bold text-white">Regole di Validità</h3>
                        </div>

                        {/* Date Range */}
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-3 ml-1">Periodo di Validità</label>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="group">
                                    <label htmlFor="validFrom" className="block text-xs uppercase font-bold text-slate-500 mb-1 ml-1 group-focus-within:text-indigo-400 transition-colors">Dal</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{Icons.Calendar}</div>
                                        <input
                                            type="date" id="validFrom" name="validFrom"
                                            value={formData.validFrom} onChange={handleInputChange}
                                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                </div>
                                <div className="group">
                                    <label htmlFor="validTo" className="block text-xs uppercase font-bold text-slate-500 mb-1 ml-1 group-focus-within:text-indigo-400 transition-colors">Al</label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">{Icons.Calendar}</div>
                                        <input
                                            type="date" id="validTo" name="validTo"
                                            value={formData.validTo} onChange={handleInputChange}
                                            className="w-full bg-slate-900/50 border border-slate-700 text-white rounded-xl py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Time Slots */}
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-3 ml-1">Fasce Orarie Giornaliere</label>
                            <div className="space-y-3">
                                {formData.timeSlots.map((slot, index) => (
                                    <div key={index} className="flex items-center gap-3 p-3 bg-slate-900/30 rounded-xl border border-slate-700/30 transition-all hover:border-slate-600">
                                        <div className="flex-1 grid grid-cols-2 gap-3">
                                            <input
                                                type="time" value={slot.from}
                                                onChange={(e) => handleTimeSlotChange(index, 'from', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-center focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                            />
                                            <input
                                                type="time" value={slot.to}
                                                onChange={(e) => handleTimeSlotChange(index, 'to', e.target.value)}
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-white text-center focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                            />
                                        </div>
                                        <button
                                            type="button" onClick={() => removeTimeSlot(index)}
                                            className={`p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors ${formData.timeSlots.length <= 1 ? 'opacity-0 pointer-events-none' : ''}`}
                                            title="Rimuovi fascia"
                                        >
                                            {Icons.Trash}
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                type="button" onClick={addTimeSlot}
                                className="mt-4 flex items-center space-x-2 text-sm text-indigo-400 hover:text-indigo-300 font-semibold px-2 py-1 rounded-lg hover:bg-indigo-500/10 transition-colors"
                            >
                                {Icons.Plus}
                                <span>Aggiungi fascia oraria</span>
                            </button>
                        </div>

                        {/* One Shot Option */}
                        {creationMode === "single" && id === undefined && (
                            <div className="p-5 bg-slate-900/30 rounded-2xl border border-slate-700/30">
                                <label className="block text-sm font-medium text-white mb-4">È un QR Code "One-shot"?</label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div
                                        onClick={() => handleRadioChange('isOneShot', 'no')}
                                        className={`cursor-pointer p-4 rounded-xl border transition-all flex items-center justify-between ${!formData.isOneShot ? 'bg-indigo-600/20 border-indigo-500 ring-1 ring-indigo-500' : 'bg-slate-800 border-slate-700 hover:border-slate-600'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!formData.isOneShot ? 'border-indigo-400' : 'border-slate-500'}`}>
                                                {!formData.isOneShot && <div className="w-2.5 h-2.5 bg-indigo-400 rounded-full" />}
                                            </div>
                                            <span className={`font-medium ${!formData.isOneShot ? 'text-white' : 'text-slate-400'}`}>Standard</span>
                                        </div>
                                        {!formData.isOneShot && <span className="text-xs text-indigo-300 font-semibold uppercase">Selezionato</span>}
                                    </div>

                                    <div
                                        onClick={() => handleRadioChange('isOneShot', 'yes')}
                                        className={`cursor-pointer p-4 rounded-xl border transition-all flex items-center justify-between ${formData.isOneShot ? 'bg-indigo-600/20 border-indigo-500 ring-1 ring-indigo-500' : 'bg-slate-800 border-slate-700 hover:border-slate-600'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${formData.isOneShot ? 'border-indigo-400' : 'border-slate-500'}`}>
                                                {formData.isOneShot && <div className="w-2.5 h-2.5 bg-indigo-400 rounded-full" />}
                                            </div>
                                            <span className={`font-medium ${formData.isOneShot ? 'text-white' : 'text-slate-400'}`}>One-Shot</span>
                                        </div>
                                        {formData.isOneShot && <span className="text-xs text-indigo-300 font-semibold uppercase">Selezionato</span>}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quantity for One Shot */}
                        {formData.isOneShot && creationMode === "single" && (
                            <div className="animate-in fade-in slide-in-from-top-2">
                                <label htmlFor="quantity" className="block text-sm font-medium text-slate-300 mb-2 ml-1">Numero di codici da generare</label>
                                <input
                                    type="number" id="quantity" name="quantity"
                                    value={formData.quantity} onChange={handleInputChange} min="1"
                                    disabled={idshot !== undefined}
                                    className="w-full sm:w-1/3 bg-slate-900/50 border border-slate-700 text-white rounded-xl py-2.5 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                                />
                            </div>
                        )}

                        {/* Usage Limits */}
                        {!formData.isOneShot && (
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-in fade-in">
                                {/* Total Limits */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-3">Limite Utilizzi Totale</label>
                                    <div className="flex gap-2 p-1 bg-slate-900/50 rounded-lg border border-slate-700/50">
                                        <button
                                            type="button"
                                            onClick={() => handleRadioChange('usageLimit', 'unlimited')}
                                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${formData.usageLimit === 'unlimited' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        >
                                            Illimitati
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRadioChange('usageLimit', 'limited')}
                                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${formData.usageLimit === 'limited' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        >
                                            Specifico
                                        </button>
                                    </div>
                                    {formData.usageLimit === 'limited' && (
                                        <div className="mt-3">
                                            <input
                                                type="number" id="maxUses" name="maxUses"
                                                value={formData.maxUses === -1 ? '' : formData.maxUses}
                                                onChange={handleInputChange} placeholder="N. max utilizzi"
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg py-2 px-4 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Fasce Limits */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-3">Limite per Fascia Oraria</label>
                                    <div className="flex gap-2 p-1 bg-slate-900/50 rounded-lg border border-slate-700/50">
                                        <button
                                            type="button"
                                            onClick={() => handleRadioChange('usageLimitFasce', 'unlimited')}
                                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${formData.usageLimitFasce === 'unlimited' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        >
                                            Illimitati
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleRadioChange('usageLimitFasce', 'limited')}
                                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${formData.usageLimitFasce === 'limited' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-white'}`}
                                        >
                                            Specifico
                                        </button>
                                    </div>
                                    {formData.usageLimitFasce === 'limited' && (
                                        <div className="mt-3">
                                            <input
                                                type="number" id="maxUsesFasce" name="maxUsesFasce"
                                                value={formData.maxUsesFasce === -1 ? '' : formData.maxUsesFasce}
                                                onChange={handleInputChange} placeholder="N. max per fascia"
                                                className="w-full bg-slate-800 border border-slate-600 rounded-lg py-2 px-4 text-white focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer / Actions */}
                <div className="sticky bottom-4 z-20">
                    <div className="absolute inset-0 bg-slate-900/50 blur-xl -m-4"></div>
                    <div className="relative bg-slate-800/90 backdrop-blur-xl border border-slate-700 shadow-2xl rounded-2xl p-4 flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="text-sm text-slate-400 hidden sm:block">
                            {submitError ? <span className="text-red-400 font-medium bg-red-900/20 px-3 py-1 rounded-lg">{submitError}</span> : <span>Controlla i dati prima di confermare.</span>}
                        </div>
                        {submitError && <div className="text-red-400 text-sm sm:hidden w-full text-center">{submitError}</div>}

                        <div className="flex space-x-3 w-full sm:w-auto">
                            <button
                                type="button" onClick={() => setFormData(initialFormData)}
                                className="flex-1 sm:flex-none px-6 py-2.5 rounded-xl text-slate-300 font-semibold hover:bg-slate-700 hover:text-white transition-colors"
                            >
                                Reset
                            </button>
                            <button
                                type="submit" disabled={isSubmitting}
                                className="flex-1 sm:flex-none px-8 py-2.5 rounded-xl text-white font-bold bg-gradient-to-r from-sky-600 to-indigo-600 hover:from-sky-500 hover:to-indigo-500 shadow-lg shadow-indigo-500/30 transition-all transform active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        <span>Attendi...</span>
                                    </>
                                ) : (
                                    <span>{id ? 'Salva Modifiche' : 'Genera QR Code'}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>

            {/* Modals */}
            {createdQrCodesList && (
                <QrCodeListDisplayModal
                    qrCodes={createdQrCodesList}
                    onClose={() => setCreatedQrCodesList(null)}
                    download={true}
                />
            )}

            {createdQrCodesDifferentList && (
                <QrCodeListDisplayModal
                    qrCodes={createdQrCodesDifferentList}
                    onClose={() => setCreatedQrCodesDifferentList(null)}
                    download={true}
                />
            )}

            {!createdQrCodesList && createdQrCode && (
                <QrCodeDisplayModal
                    qrCode={createdQrCode}
                    onClose={() => setCreatedQrCode(null)}
                />
            )}
        </div>
    );
};

export default CreateQrPage;